/*
 * pcx-capture-web-vitals.js — Core Web Vitals capture module.
 *
 *   Halide-portable (spec replay-v2 R6): this module has ZERO Shopify/Liquid
 *   dependencies. It does not read window.PeterCox, Liquid globals, or any
 *   theme-specific symbol. All wiring (where to send events, which web-vitals
 *   library is present) is injected via init(deps) by the adapter
 *   (pcx-capture-adapter.js — the single Shopify-glue file).
 *
 *   Uses the vendored Google web-vitals library (assets/web-vitals.iife.js,
 *   pinned 5.3.0 from the live npm registry 2026-06-09 — v5 API: onLCP/onCLS/
 *   onINP/onTTFB; FID was removed in v5). The library is responsible for the
 *   correct INP/LCP/CLS measurement semantics (buffered entries, bfcache,
 *   final-value reporting) that a hand-rolled PerformanceObserver gets wrong.
 *
 *   Integration:
 *     PeterCoxCapture.webVitals.init({
 *       track: fn(eventType, properties),   // REQUIRED — the send sink
 *       vitals: window.webVitals,           // REQUIRED — the web-vitals lib global
 *       navigationType: 'navigate'          // OPTIONAL — page navigation type
 *     });
 */
(function () {
  'use strict';

  /* PURE HELPERS BEGIN — extracted + unit-tested in tests/pcx-capture-web-vitals.test.mjs */

  // Round a metric value for transport. CLS is a small unitless score (keep 4
  // decimals); timing metrics are milliseconds (integer is enough and keeps
  // the payload small). Returns null for non-finite input so a bad reading is
  // dropped, never sent as NaN/Infinity (the server rejects non-numeric).
  function roundMetric(name, value) {
    if (typeof value !== 'number' || !isFinite(value)) return null;
    if (name === 'cls') return Math.round(value * 10000) / 10000;
    return Math.round(value);
  }

  // Map a web-vitals metric object {name, value, ...} to the property key the
  // collector expects (lowercase metric name). Unknown metric names map to
  // null so only the four C-spec vitals (LCP/CLS/INP/TTFB) are ever sent.
  var VITAL_KEYS = { LCP: 'lcp', CLS: 'cls', INP: 'inp', TTFB: 'ttfb' };
  function vitalKey(name) {
    return Object.prototype.hasOwnProperty.call(VITAL_KEYS, name)
      ? VITAL_KEYS[name] : null;
  }

  // Build the properties payload for one resolved vital. Returns null when the
  // metric is not one we report or its value is unusable — the caller then
  // sends nothing.
  function buildVitalProperties(metric, navigationType) {
    if (!metric || typeof metric.name !== 'string') return null;
    var key = vitalKey(metric.name);
    if (!key) return null;
    var rounded = roundMetric(key, metric.value);
    if (rounded === null) return null;
    var props = {};
    props[key] = rounded;
    if (navigationType) props.navigation_type = String(navigationType);
    return props;
  }

  /* PURE HELPERS END */

  function init(deps) {
    deps = deps || {};
    var track = deps.track;
    var vitals = deps.vitals;
    if (typeof track !== 'function' || !vitals) return false;

    function report(metric) {
      var props = buildVitalProperties(metric, deps.navigationType);
      if (props) track('web_vitals', props);
    }

    // v5 API. Each fires once per page-load with the final value (the library
    // handles buffering + final-value reporting). FID is intentionally absent
    // (removed in web-vitals v5 — INP is the responsiveness metric now).
    if (typeof vitals.onLCP === 'function') vitals.onLCP(report);
    if (typeof vitals.onCLS === 'function') vitals.onCLS(report);
    if (typeof vitals.onINP === 'function') vitals.onINP(report);
    if (typeof vitals.onTTFB === 'function') vitals.onTTFB(report);
    return true;
  }

  window.PeterCoxCapture = window.PeterCoxCapture || {};
  window.PeterCoxCapture.webVitals = { init: init };
})();
